import { Stack, TextField, Typography } from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { FormikProps, useFormik } from 'formik';
import { forwardRef, Ref, useImperativeHandle, useMemo } from 'react';
import * as yup from 'yup';

import {
  CalendarReminder,
  CreateCalendarReminder,
  UpdateCalendarReminder,
} from '@/models/CalendarEntry';
import { useTranslationPrefix } from '@/translations/i18n';

export type EditCalendarReminderFormValues = CreateCalendarReminder | UpdateCalendarReminder;

export type EditCalendarReminderFormRef<T extends EditCalendarReminderFormValues> = FormikProps<T>;

export interface EditCalendarReminderFormProps<T extends EditCalendarReminderFormValues> {
  /**
   * The reminder to be edited. Should not be provided when creating a reminder.
   */
  reminder: T extends CreateCalendarReminder
    ? undefined
    : T extends UpdateCalendarReminder
    ? CalendarReminder | null
    : never;
  /**
   * Callback fired when the form is submitted.
   */
  onSubmit: (values: T) => void;
}

const EditCalendarReminderFormComponent = <T extends EditCalendarReminderFormValues>(
  { reminder, onSubmit }: EditCalendarReminderFormProps<T>,
  ref: Ref<EditCalendarReminderFormRef<T>>,
) => {
  const t = useTranslationPrefix('home.calendar.reminders.edit_form');
  const validationT = useTranslationPrefix('validation');

  const initialValues = useMemo(
    () =>
      ({
        name: reminder?.name ?? '',
        startDate: reminder?.startDate,
      } as T),
    [reminder],
  );

  const validationSchema = yup.object({
    name: yup.string().required(validationT('required', { name: t('name') })),
    startDate: yup.number().required(validationT('required', { name: t('start_date') })),
  });

  const formik = useFormik<T>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    onSubmit,
  });

  const parsedStartDate = useMemo(
    () => (formik.values.startDate ? new Date(formik.values.startDate) : null),
    [formik.values.startDate],
  );

  useImperativeHandle(ref, () => formik);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={2.5}>
          <Stack spacing={0.75}>
            <Typography
              component="label"
              htmlFor="name-field"
              variant="body2"
              sx={{ fontSize: 14 }}>
              {t('name')}
            </Typography>
            <TextField
              id="name-field"
              name="name"
              aria-label={t('name')}
              fullWidth
              required
              autoFocus
              size="small"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && (formik.errors.name as string)}
              FormHelperTextProps={{ style: { fontSize: 11 } }}
            />
          </Stack>
          <Stack spacing={0.75}>
            <Typography
              component="label"
              htmlFor="startDate-field"
              variant="body2"
              sx={{ fontSize: 14 }}>
              {t('start_date')}
            </Typography>
            <DateTimePicker
              name="startDate"
              aria-label={t('start_date')}
              disableHighlightToday
              value={parsedStartDate}
              format={t('start_date_format')}
              onChange={(date) => formik.setFieldValue('startDate', date?.getTime())}
              slots={{
                actionBar: () => null,
              }}
              slotProps={{
                popper: {
                  sx: {
                    zIndex: (theme) => theme.zIndex.modal + 400,
                  },
                },
                desktopPaper: {
                  sx: {
                    zIndex: (theme) => theme.zIndex.modal + 401,
                  },
                },
                mobilePaper: {
                  sx: {
                    zIndex: (theme) => theme.zIndex.modal + 401,
                  },
                },
                dialog: {
                  sx: {
                    zIndex: (theme) => theme.zIndex.modal + 400,
                  },
                },
                textField: {
                  id: 'startDate-field',
                  fullWidth: true,
                  required: true,
                  size: 'small',
                  placeholder: t('start_date_placeholder'),
                  error: formik.touched.startDate && Boolean(formik.errors.startDate),
                  helperText: formik.touched.startDate && (formik.errors.startDate as string),
                  FormHelperTextProps: { style: { fontSize: 11 } },
                },
              }}
            />
          </Stack>
        </Stack>
      </form>
    </LocalizationProvider>
  );
};

export const EditCalendarReminderForm = forwardRef(EditCalendarReminderFormComponent) as <
  T extends EditCalendarReminderFormValues,
>(
  props: EditCalendarReminderFormProps<T> & { ref?: Ref<EditCalendarReminderFormRef<T>> },
) => ReturnType<typeof EditCalendarReminderFormComponent<T>>;
